import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useParams } from 'wouter';
import {
  ArrowLeft,
  Clock,
  Phone,
  Mail,
  MessageSquare,
  Send,
  AlertTriangle,
  CheckCircle,
  Lightbulb,
  ExternalLink,
  User,
  Building,
  ImagePlus,
  Trash2,
  X,
  Loader2,
  Plus,
  Tag,
  Link2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import {
  fetchTicket,
  updateTicket,
  addActivity,
  findSimilarSolutions,
  fetchRelatedByTags,
  fetchTicketImages,
  uploadTicketImage,
  deleteTicketImage,
  getTicketImageUrl,
  fetchProviders,
  createProvider,
  updateTicketTags,
  sendTicketReply,
  type SupportTicket,
  type SupportTicketActivity,
  type SimilarSolution,
  type SupportTicketImage,
  type TicketTag,
  type RelatedTicket,
} from '@/lib/api/support-tickets';
import { TicketTagInput } from '@/components/support/TicketTagInput';

const NONE_VALUE = '__NONE__';

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'escalated', label: 'Escalated' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

const ACTIVITY_TYPES = [
  { value: 'comment', label: 'Comment', icon: MessageSquare },
  { value: 'call', label: 'Phone Call', icon: Phone },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'escalation', label: 'Escalation', icon: AlertTriangle },
];

function StatusBadge({ status }: { status: string }) {
  const variants: Record<string, string> = {
    open: 'bg-blue-100 text-blue-800',
    in_progress: 'bg-yellow-100 text-yellow-800',
    escalated: 'bg-red-100 text-red-800',
    resolved: 'bg-green-100 text-green-800',
    closed: 'bg-gray-100 text-gray-800',
  };

  const labels: Record<string, string> = {
    open: 'Open',
    in_progress: 'In Progress',
    escalated: 'Escalated',
    resolved: 'Resolved',
    closed: 'Closed',
  };

  return (
    <Badge className={variants[status] || variants.open} variant="secondary">
      {labels[status] || status}
    </Badge>
  );
}

function ActivityIcon({ type }: { type: string }) {
  const icons: Record<string, React.ElementType> = {
    comment: MessageSquare,
    call: Phone,
    email: Mail,
    escalation: AlertTriangle,
    status_change: CheckCircle,
  };

  const Icon = icons[type] || MessageSquare;
  return <Icon className="h-4 w-4" />;
}

// Type for parsed email activity content
interface EmailActivityData {
  direction: 'inbound' | 'outbound';
  from?: string;
  fromName?: string;
  to?: string;
  toName?: string;
  subject?: string;
  body?: string;
  summary?: string;
  emailType?: 'acknowledgment' | 'status_update' | 'resolution' | 'manual_reply';
  category?: string;
  aiConfidence?: number;
  aiSummary?: string;
  newStatus?: string;
  resolution?: string;
}

// Robust JSON parsing for email activity content
function tryParseEmailJson(content: string): EmailActivityData | null {
  if (!content || typeof content !== 'string') return null;

  // Try direct parse first
  try {
    const parsed = JSON.parse(content);
    if (parsed && typeof parsed === 'object' && (parsed.direction || parsed.body || parsed.from)) {
      return parsed as EmailActivityData;
    }
  } catch {
    // Continue to fallbacks
  }

  // Try double-encoded JSON (content might be escaped)
  try {
    const unescaped = content.replace(/\\"/g, '"').replace(/\\n/g, '\n');
    const parsed = JSON.parse(unescaped);
    if (parsed && typeof parsed === 'object' && (parsed.direction || parsed.body || parsed.from)) {
      return parsed as EmailActivityData;
    }
  } catch {
    // Not double-encoded
  }

  // Check if content looks like it should be JSON but failed
  if (content.trim().startsWith('{') && content.trim().endsWith('}')) {
    // Try to fix common JSON issues (unescaped newlines, etc.)
    try {
      const fixed = content
        .replace(/[\n\r]/g, '\\n')
        .replace(/\t/g, '\\t');
      const parsed = JSON.parse(fixed);
      if (parsed && typeof parsed === 'object') {
        return parsed as EmailActivityData;
      }
    } catch {
      // Truly malformed
    }
  }

  return null;
}

function EmailActivityDisplay({ data }: { data: EmailActivityData }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const isInbound = data.direction === 'inbound';

  const getEmailTypeLabel = () => {
    if (data.emailType === 'acknowledgment') return 'Auto-Reply';
    if (data.emailType === 'status_update') return 'Status Update';
    if (data.emailType === 'resolution') return 'Resolution';
    if (data.emailType === 'manual_reply') return 'Support Reply';
    return isInbound ? 'Customer Email' : 'Support Email';
  };

  return (
    <div className={`rounded-lg border ${isInbound ? 'border-blue-200 bg-blue-50/30 dark:border-blue-800 dark:bg-blue-950/30' : 'border-green-200 bg-green-50/30 dark:border-green-800 dark:bg-green-950/30'}`}>
      <div className="p-3">
        {/* Email header */}
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-xs font-medium px-2 py-0.5 rounded ${isInbound ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' : 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'}`}>
                {getEmailTypeLabel()}
              </span>
              {data.aiConfidence && (
                <span className="text-xs text-muted-foreground">
                  AI: {data.aiConfidence}%
                </span>
              )}
            </div>
            <p className="text-sm">
              <span className="text-muted-foreground">{isInbound ? 'From:' : 'To:'}</span>{' '}
              <span className="font-medium">{data.fromName || data.from || data.to}</span>
              {(data.from || data.to) && <span className="text-muted-foreground text-xs ml-1">({data.from || data.to})</span>}
            </p>
            {data.subject && (
              <p className="text-sm">
                <span className="text-muted-foreground">Subject:</span>{' '}
                <span>{data.subject}</span>
              </p>
            )}
          </div>
        </div>

        {/* Email body */}
        {data.body && (
          <div className="mt-2">
            <div
              className={`text-sm whitespace-pre-wrap bg-white dark:bg-gray-900 rounded p-3 border ${isExpanded ? '' : 'max-h-32 overflow-hidden relative'}`}
            >
              {data.body}
              {!isExpanded && data.body.length > 300 && (
                <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white dark:from-gray-900 to-transparent" />
              )}
            </div>
            {data.body.length > 300 && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="text-xs text-blue-600 hover:underline mt-1"
              >
                {isExpanded ? 'Show less' : 'Show more'}
              </button>
            )}
          </div>
        )}

        {/* AI Summary for inbound emails */}
        {data.aiSummary && (
          <div className="mt-2 p-2 bg-amber-50 dark:bg-amber-950/30 rounded border border-amber-200 dark:border-amber-800">
            <p className="text-xs text-amber-700 dark:text-amber-300">
              <span className="font-medium">Summary:</span> {data.aiSummary}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// Fallback display for legacy/plain-text emails or when JSON parsing fails
function LegacyEmailDisplay({ content }: { content: string }) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Clean the content - remove JSON artifacts if present and format nicely
  const cleanContent = content
    // Try to extract just the body if it looks like broken JSON
    .replace(/^\s*\{\s*"direction"\s*:\s*"[^"]*"\s*,/i, '')
    .replace(/^\s*"from"\s*:\s*"[^"]*"\s*,/i, '')
    .replace(/^\s*"fromName"\s*:\s*"[^"]*"\s*,/i, '')
    .replace(/^\s*"subject"\s*:\s*"[^"]*"\s*,/i, '')
    .replace(/^\s*"body"\s*:\s*"/i, '')
    .replace(/"\s*,?\s*"summary"\s*:\s*"[^"]*"\s*,?/i, '')
    .replace(/"\s*,?\s*"category"\s*:\s*"[^"]*"\s*,?/i, '')
    .replace(/"\s*,?\s*"aiConfidence"\s*:\s*\d*\s*,?/i, '')
    .replace(/"\s*,?\s*"aiSummary"\s*:\s*"[^"]*"\s*,?/i, '')
    .replace(/"\s*,?\s*"emailType"\s*:\s*"[^"]*"\s*,?/i, '')
    .replace(/"\s*\}\s*$/i, '')
    // Unescape common escape sequences
    .replace(/\\n/g, '\n')
    .replace(/\\r/g, '')
    .replace(/\\t/g, '\t')
    .replace(/\\"/g, '"')
    .replace(/\\\\/g, '\\')
    // Clean up excessive whitespace
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  // If cleaning didn't help much and it still looks like JSON, show a simplified message
  const displayContent = cleanContent.startsWith('{') && cleanContent.includes('"')
    ? 'Email content (see ticket details)'
    : cleanContent;

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50/30 dark:border-gray-700 dark:bg-gray-900/30 p-3">
      <div className={`text-sm whitespace-pre-wrap leading-relaxed ${isExpanded ? '' : 'max-h-32 overflow-hidden relative'}`}>
        {displayContent}
        {!isExpanded && displayContent.length > 300 && (
          <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-gray-50 dark:from-gray-900 to-transparent" />
        )}
      </div>
      {displayContent.length > 300 && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-xs text-blue-600 hover:underline mt-1"
        >
          {isExpanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}

function TimelineItem({ activity }: { activity: SupportTicketActivity }) {
  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isEmail = activity.activityType === 'email';

  // Try to parse JSON email content with robust parsing
  const emailData = isEmail ? tryParseEmailJson(activity.content) : null;

  const isInboundEmail = emailData?.direction === 'inbound' ||
    (isEmail && !emailData && activity.content.toLowerCase().includes('customer reply'));
  const isOutboundEmail = emailData?.direction === 'outbound' ||
    (isEmail && !emailData && !activity.content.toLowerCase().includes('customer reply') && !activity.content.toLowerCase().includes('ticket created'));

  return (
    <div className={`flex gap-4 pb-6 last:pb-0`}>
      <div className="flex flex-col items-center">
        <div className={`flex h-8 w-8 items-center justify-center rounded-full ${isEmail ? (isInboundEmail ? 'bg-blue-100 dark:bg-blue-900' : 'bg-green-100 dark:bg-green-900') : 'bg-muted'}`}>
          <ActivityIcon type={activity.activityType} />
        </div>
        <div className="flex-1 w-px bg-border mt-2" />
      </div>
      <div className="flex-1 pb-2">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-medium capitalize">
            {activity.activityType.replace('_', ' ')}
          </span>
          {isInboundEmail && (
            <Badge variant="outline" className="text-blue-600 border-blue-300">
              Inbound
            </Badge>
          )}
          {isOutboundEmail && (
            <Badge variant="outline" className="text-green-600 border-green-300">
              Outbound
            </Badge>
          )}
          {activity.isResolutionStep && (
            <Badge variant="outline" className="text-green-600">
              Resolution Step
            </Badge>
          )}
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          {formatDateTime(activity.activityTime || activity.createdAt)}
          {activity.createdByName && ` by ${activity.createdByName}`}
        </p>

        {/* Render email activity with rich display or fallback */}
        {emailData ? (
          <EmailActivityDisplay data={emailData} />
        ) : isEmail ? (
          <LegacyEmailDisplay content={activity.content} />
        ) : (
          <p className="text-sm whitespace-pre-wrap">{activity.content}</p>
        )}
      </div>
    </div>
  );
}

function SuggestionCard({ suggestion }: { suggestion: SimilarSolution }) {
  return (
    <Card className="bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <span className="font-medium">{suggestion.ticketNumber}</span>
          <Badge variant="outline">{suggestion.similarity}% match</Badge>
        </div>
        <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
          {suggestion.issueDescription}
        </p>
        {suggestion.resolution && (
          <div className="mt-2 p-2 bg-white dark:bg-gray-900 rounded-md">
            <p className="text-xs font-medium text-green-600 mb-1">Resolution:</p>
            <p className="text-sm">{suggestion.resolution}</p>
          </div>
        )}
        {suggestion.provider && (
          <p className="text-xs text-muted-foreground mt-2">
            Provider: {suggestion.provider}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default function SupportTicketDetailPage() {
  const params = useParams<{ id: string }>();
  const ticketId = parseInt(params.id || '0');
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [newActivity, setNewActivity] = useState({
    activityType: 'comment',
    content: '',
    isResolutionStep: false,
  });
  const [replyMode, setReplyMode] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedImage, setSelectedImage] = useState<SupportTicketImage | null>(null);
  const [showNewProviderDialog, setShowNewProviderDialog] = useState(false);
  const [newProviderName, setNewProviderName] = useState('');
  const [ticketTags, setTicketTags] = useState<TicketTag[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: ticket, isLoading } = useQuery({
    queryKey: ['support-ticket', ticketId],
    queryFn: () => fetchTicket(ticketId),
    enabled: !!ticketId,
  });

  const { data: suggestionsData, isLoading: loadingSuggestions } = useQuery({
    queryKey: ['ticket-suggestions', ticketId],
    queryFn: () => findSimilarSolutions(ticketId),
    enabled: showSuggestions && !!ticketId,
  });

  const { data: images = [], isLoading: loadingImages } = useQuery({
    queryKey: ['support-ticket-images', ticketId],
    queryFn: () => fetchTicketImages(ticketId),
    enabled: !!ticketId,
  });

  const { data: providers = [] } = useQuery({
    queryKey: ['equipment-providers'],
    queryFn: () => fetchProviders(true),
  });

  const { data: relatedTickets = [], isLoading: loadingRelated } = useQuery({
    queryKey: ['related-tickets-by-tags', ticketId],
    queryFn: () => fetchRelatedByTags(ticketId),
    enabled: !!ticketId && ticketTags.length > 0,
  });

  // Sync tags from ticket data
  useEffect(() => {
    if (ticket?.tags) {
      setTicketTags(ticket.tags);
    }
  }, [ticket?.tags]);

  const handleTagsChange = async (newTags: TicketTag[]) => {
    setTicketTags(newTags);
    if (ticketId) {
      try {
        await updateTicketTags(ticketId, newTags.map((t) => t.id));
        queryClient.invalidateQueries({ queryKey: ['support-ticket', ticketId] });
        queryClient.invalidateQueries({ queryKey: ['support-tickets'] });
        queryClient.invalidateQueries({ queryKey: ['related-tickets-by-tags', ticketId] });
      } catch {
        toast({ title: 'Failed to update tags', variant: 'destructive' });
      }
    }
  };

  const uploadImageMutation = useMutation({
    mutationFn: (file: File) => uploadTicketImage(ticketId, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support-ticket-images', ticketId] });
      toast({ title: 'Image uploaded successfully' });
    },
    onError: () => {
      toast({ title: 'Failed to upload image', variant: 'destructive' });
    },
  });

  const deleteImageMutation = useMutation({
    mutationFn: (imageId: number) => deleteTicketImage(ticketId, imageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support-ticket-images', ticketId] });
      setSelectedImage(null);
      toast({ title: 'Image deleted' });
    },
    onError: () => {
      toast({ title: 'Failed to delete image', variant: 'destructive' });
    },
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadImageMutation.mutate(file);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const updateMutation = useMutation({
    mutationFn: (data: Partial<SupportTicket>) => updateTicket(ticketId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support-ticket', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['support-tickets'] });
      queryClient.invalidateQueries({ queryKey: ['support-ticket-stats'] });
      toast({ title: 'Ticket updated' });
    },
    onError: () => {
      toast({ title: 'Failed to update ticket', variant: 'destructive' });
    },
  });

  const createProviderMutation = useMutation({
    mutationFn: createProvider,
    onSuccess: (provider) => {
      queryClient.invalidateQueries({ queryKey: ['equipment-providers'] });
      // Update the ticket with the new provider
      updateMutation.mutate({
        systemProvider: provider.name,
        providerId: provider.id
      });
      setShowNewProviderDialog(false);
      setNewProviderName('');
      toast({ title: `Provider "${provider.name}" added` });
    },
    onError: () => {
      toast({ title: 'Failed to create provider', variant: 'destructive' });
    },
  });

  const addActivityMutation = useMutation({
    mutationFn: (data: typeof newActivity) => addActivity(ticketId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support-ticket', ticketId] });
      setNewActivity({ activityType: 'comment', content: '', isResolutionStep: false });
      setReplyMode(false);
      toast({ title: 'Activity added' });
    },
    onError: () => {
      toast({ title: 'Failed to add activity', variant: 'destructive' });
    },
  });

  const sendReplyMutation = useMutation({
    mutationFn: (content: string) => sendTicketReply(ticketId, { content }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support-ticket', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['support-tickets'] });
      setNewActivity({ activityType: 'comment', content: '', isResolutionStep: false });
      setReplyMode(false);
      toast({ title: 'Reply sent to customer' });
    },
    onError: (error) => {
      toast({
        title: 'Failed to send reply',
        description: error instanceof Error ? error.message : undefined,
        variant: 'destructive',
      });
    },
  });

  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <p className="text-muted-foreground">Loading ticket...</p>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="container mx-auto p-6">
        <p className="text-muted-foreground">Ticket not found</p>
      </div>
    );
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const replyRecipient = ticket.sourceEmail || ticket.displayCustomerEmail || ticket.customerEmail;
  const replyRecipientName = ticket.displayCustomerName || ticket.customerName || 'Customer';

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setLocation('/support-tickets')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">{ticket.ticketNumber}</h1>
            <StatusBadge status={ticket.status} />
            {ticket.sourceEmail && (
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                <Mail className="h-3 w-3 mr-1" />
                Email Ticket
              </Badge>
            )}
            {ticketTags.map((tag) => (
              <Badge
                key={tag.id}
                className="text-xs text-white"
                style={{ backgroundColor: tag.color || '#6b7280' }}
              >
                {tag.name}
              </Badge>
            ))}
          </div>
          <p className="text-muted-foreground">
            Created {formatDate(ticket.createdAt)}
            {ticket.sourceEmail && ` from ${ticket.sourceEmail}`}
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => setShowSuggestions(!showSuggestions)}
        >
          <Lightbulb className="h-4 w-4 mr-2" />
          {showSuggestions ? 'Hide' : 'Find'} Similar Solutions
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Issue Details */}
          <Card>
            <CardHeader>
              <CardTitle>Issue Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Original Email Subject */}
              {ticket.originalSubject && (
                <div className="p-3 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800">
                  <label className="text-sm font-medium text-blue-700 dark:text-blue-300 flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Original Email Subject
                  </label>
                  <p className="mt-1">{ticket.originalSubject}</p>
                  {ticket.lastCustomerEmailAt && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Last email received: {formatDate(ticket.lastCustomerEmailAt)}
                    </p>
                  )}
                </div>
              )}
              <div>
                <label className="text-sm font-medium">Description</label>
                <p className="mt-1 whitespace-pre-wrap">{ticket.issueDescription}</p>
              </div>
              <div>
                <label className="text-sm font-medium">Equipment Provider</label>
                <div className="flex gap-2 mt-1">
                  <Select
                    value={ticket.systemProvider || NONE_VALUE}
                    onValueChange={(providerName) => {
                      if (providerName === NONE_VALUE) {
                        updateMutation.mutate({
                          systemProvider: null,
                          providerId: null
                        });
                      } else {
                        const provider = providers.find(p => p.name === providerName);
                        updateMutation.mutate({
                          systemProvider: providerName,
                          providerId: provider?.id || null
                        });
                      }
                    }}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Select provider" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE_VALUE}>None</SelectItem>
                      {providers.map((provider) => (
                        <SelectItem key={provider.id} value={provider.name}>
                          {provider.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setShowNewProviderDialog(true)}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              {ticket.providerTicketRef && (
                <div>
                  <label className="text-sm font-medium">Provider Reference</label>
                  <p className="mt-1 flex items-center gap-2">
                    <ExternalLink className="h-4 w-4 text-muted-foreground" />
                    {ticket.providerTicketRef}
                  </p>
                </div>
              )}
              {ticket.resolution && (
                <div className="p-4 bg-green-50 dark:bg-green-950 rounded-lg">
                  <label className="text-sm font-medium text-green-700 dark:text-green-300">
                    Resolution
                  </label>
                  <p className="mt-1 whitespace-pre-wrap">{ticket.resolution}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Images Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <ImagePlus className="h-5 w-5" />
                  Images
                </CardTitle>
                <div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept="image/*"
                    className="hidden"
                    data-testid="input-image-upload"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadImageMutation.isPending}
                    data-testid="button-upload-image"
                  >
                    {uploadImageMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <ImagePlus className="h-4 w-4 mr-2" />
                        Upload Image
                      </>
                    )}
                  </Button>
                </div>
              </div>
              <CardDescription>
                Attach photos from the customer to help diagnose the issue
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingImages ? (
                <p className="text-muted-foreground">Loading images...</p>
              ) : images.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  No images attached yet
                </p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {images.map((image) => (
                    <div
                      key={image.id}
                      className="relative group cursor-pointer rounded-lg overflow-hidden border"
                      onClick={() => setSelectedImage(image)}
                      data-testid={`image-thumbnail-${image.id}`}
                    >
                      <img
                        src={getTicketImageUrl(ticketId, image.id)}
                        alt={image.originalFilename}
                        className="w-full h-32 object-cover"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <p className="text-white text-xs text-center px-2 truncate">
                          {image.originalFilename}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Image Preview Modal */}
          {selectedImage && (
            <div
              className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
              onClick={() => setSelectedImage(null)}
            >
              <div
                className="relative max-w-4xl w-full bg-white dark:bg-gray-900 rounded-lg overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between p-4 border-b">
                  <h3 className="font-medium truncate">{selectedImage.originalFilename}</h3>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => deleteImageMutation.mutate(selectedImage.id)}
                      disabled={deleteImageMutation.isPending}
                      data-testid="button-delete-image"
                    >
                      {deleteImageMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedImage(null)}
                      data-testid="button-close-image-preview"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="p-4 flex items-center justify-center max-h-[70vh] overflow-auto">
                  <img
                    src={getTicketImageUrl(ticketId, selectedImage.id)}
                    alt={selectedImage.originalFilename}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              </div>
            </div>
          )}

          {/* AI Suggestions */}
          {showSuggestions && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lightbulb className="h-5 w-5 text-yellow-500" />
                  Similar Past Issues
                </CardTitle>
                <CardDescription>
                  {suggestionsData?.aiAvailable
                    ? 'AI-powered suggestions based on past resolved tickets'
                    : 'Keyword-based matches from past tickets'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingSuggestions ? (
                  <p className="text-muted-foreground">Searching for similar issues...</p>
                ) : suggestionsData?.suggestions.length === 0 ? (
                  <p className="text-muted-foreground">No similar past issues found</p>
                ) : (
                  <div className="space-y-4">
                    {suggestionsData?.suggestions.map((suggestion) => (
                      <SuggestionCard key={suggestion.ticketId} suggestion={suggestion} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Related Tickets by Tags */}
          {ticketTags.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Link2 className="h-5 w-5" />
                  Related Tickets
                </CardTitle>
                <CardDescription>
                  Other tickets sharing the same tags
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingRelated ? (
                  <p className="text-muted-foreground">Finding related tickets...</p>
                ) : relatedTickets.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">
                    No related tickets found
                  </p>
                ) : (
                  <div className="space-y-3">
                    {relatedTickets.map((related) => (
                      <div
                        key={related.id}
                        className="p-3 rounded-lg border hover:bg-muted/50 cursor-pointer transition-colors"
                        onClick={() => setLocation(`/support-tickets/${related.id}`)}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">{related.ticketNumber}</span>
                            <StatusBadge status={related.status} />
                          </div>
                          <Badge variant="outline" className="text-xs">
                            {related.sharedTagCount} shared {Number(related.sharedTagCount) === 1 ? 'tag' : 'tags'}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-1 mb-2">
                          {related.smartSubject || related.issueDescription}
                        </p>
                        <div className="flex flex-wrap gap-1 mb-2">
                          {related.sharedTags.map((tag) => (
                            <Badge
                              key={tag.id}
                              className="text-[10px] text-white px-1.5 py-0"
                              style={{ backgroundColor: tag.color || '#6b7280' }}
                            >
                              {tag.name}
                            </Badge>
                          ))}
                        </div>
                        {related.resolution && (
                          <div className="p-2 bg-green-50 dark:bg-green-950/30 rounded text-xs">
                            <span className="font-medium text-green-700 dark:text-green-300">Resolution: </span>
                            <span className="line-clamp-2">{related.resolution}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Activity Timeline */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Activity Timeline</CardTitle>
              {replyRecipient && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setReplyMode(true);
                    setNewActivity({
                      ...newActivity,
                      activityType: 'email',
                      content: `Dear ${replyRecipientName},\n\n`,
                    });
                  }}
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Reply to Customer
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {/* Add Activity Form */}
              <div className="mb-6 p-4 bg-muted rounded-lg">
                <div className="flex gap-4 mb-3">
                  <Select
                    value={newActivity.activityType}
                    onValueChange={(val) => {
                      setReplyMode(false);
                      setNewActivity({ ...newActivity, activityType: val });
                    }}
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ACTIVITY_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={newActivity.isResolutionStep}
                      onChange={(e) =>
                        setNewActivity({
                          ...newActivity,
                          isResolutionStep: e.target.checked,
                        })
                      }
                      className="rounded"
                    />
                    Mark as resolution step
                  </label>
                </div>
                <Textarea
                  placeholder={replyMode ? 'Write your reply to the customer...' : 'Add an update...'}
                  value={newActivity.content}
                  onChange={(e) =>
                    setNewActivity({ ...newActivity, content: e.target.value })
                  }
                  rows={3}
                />
                {replyMode && (
                  <p className="mt-2 text-xs text-blue-700 dark:text-blue-300">
                    This will send an email to {replyRecipient}.
                  </p>
                )}
                <div className="mt-3 flex justify-end">
                  <Button
                    onClick={() => {
                      if (replyMode) {
                        sendReplyMutation.mutate(newActivity.content);
                      } else {
                        addActivityMutation.mutate(newActivity);
                      }
                    }}
                    disabled={
                      !newActivity.content.trim() ||
                      addActivityMutation.isPending ||
                      sendReplyMutation.isPending
                    }
                  >
                    <Send className="h-4 w-4 mr-2" />
                    {replyMode ? 'Send Reply' : 'Add Update'}
                  </Button>
                </div>
              </div>

              <Separator className="mb-6" />

              {/* Timeline */}
              {ticket.activities && ticket.activities.length > 0 ? (
                <div className="space-y-2">
                  {ticket.activities.map((activity) => (
                    <TimelineItem key={activity.id} activity={activity} />
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-4">
                  No activity yet
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Customer Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Customer
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="text-xs text-muted-foreground">Name</label>
                <p className="font-medium">
                  {ticket.displayCustomerName ||
                    ticket.linkedCustomerName ||
                    ticket.customerName ||
                    '-'}
                </p>
              </div>
              {(ticket.displayCustomerPhone || ticket.customerPhone) && (
                <div>
                  <label className="text-xs text-muted-foreground">Phone</label>
                  <p className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    {ticket.displayCustomerPhone || ticket.customerPhone}
                  </p>
                </div>
              )}
              {(ticket.displayCustomerEmail || ticket.customerEmail) && (
                <div>
                  <label className="text-xs text-muted-foreground">Email</label>
                  <p className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    {ticket.displayCustomerEmail || ticket.customerEmail}
                  </p>
                </div>
              )}
              {ticket.customerAddress && (
                <div>
                  <label className="text-xs text-muted-foreground">Address</label>
                  <p>{ticket.customerAddress}</p>
                </div>
              )}
              {ticket.customerId && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => setLocation(`/customers/${ticket.customerId}`)}
                >
                  View Customer Record
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Tags */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Tag className="h-5 w-5" />
                Tags
              </CardTitle>
            </CardHeader>
            <CardContent>
              <TicketTagInput
                selectedTags={ticketTags}
                onTagsChange={handleTagsChange}
              />
            </CardContent>
          </Card>

          {/* Status & Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Status & Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium">Status</label>
                <Select
                  value={ticket.status}
                  onValueChange={(val) => updateMutation.mutate({ status: val })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium">Provider Reference</label>
                <Input
                  key={ticket.providerTicketRef || 'empty'}
                  className="mt-1"
                  placeholder="External ticket #"
                  defaultValue={ticket.providerTicketRef || ''}
                  onBlur={(e) => {
                    if (e.target.value !== (ticket.providerTicketRef || '')) {
                      updateMutation.mutate({ providerTicketRef: e.target.value || null });
                    }
                  }}
                />
              </div>

              {ticket.status !== 'resolved' && ticket.status !== 'closed' && (
                <>
                  <Separator />
                  <div>
                    <label className="text-sm font-medium">Resolution</label>
                    <Textarea
                      className="mt-1"
                      placeholder="Enter resolution..."
                      rows={4}
                      onBlur={(e) => {
                        if (e.target.value.trim()) {
                          updateMutation.mutate({
                            resolution: e.target.value,
                            status: 'resolved',
                          });
                        }
                      }}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Entering a resolution will mark the ticket as resolved
                    </p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Timestamps */}
          <Card>
            <CardHeader>
              <CardTitle>Timestamps</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Created</span>
                <span>{formatDate(ticket.createdAt)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Updated</span>
                <span>{formatDate(ticket.updatedAt)}</span>
              </div>
              {ticket.escalatedAt && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Escalated</span>
                  <span>{formatDate(ticket.escalatedAt)}</span>
                </div>
              )}
              {ticket.resolvedAt && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Resolved</span>
                  <span>{formatDate(ticket.resolvedAt)}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* New Provider Dialog */}
      <Dialog open={showNewProviderDialog} onOpenChange={setShowNewProviderDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Provider</DialogTitle>
            <DialogDescription>
              Add a new equipment provider to the list
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Provider Name</Label>
            <Input
              value={newProviderName}
              onChange={(e) => setNewProviderName(e.target.value)}
              placeholder="e.g., SunPower, Enphase"
              className="mt-1"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowNewProviderDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={() =>
                createProviderMutation.mutate({ name: newProviderName })
              }
              disabled={!newProviderName.trim() || createProviderMutation.isPending}
            >
              Add Provider
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
